import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Modal, Form, InputNumber, Input, Select, Switch, DatePicker, message, Divider, Tag, Space, Card, Table, Row, Col, Typography, Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CloseCircleOutlined, ArrowRightOutlined, NumberOutlined, CalendarOutlined, SwapOutlined } from '@ant-design/icons';
import { inventoryService } from '@services/operator/inventoryService';
import { inventoryTypeService } from '@services/operator/inventoryTypeService';
import { inventoryAttributeDefinitionService } from '@services/operator/inventoryAttributeDefinitionService';
import { inventoryTransformationService } from '@services/operator/inventoryTransformationService';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { REASON_CODES } from '@constants/index';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

const { Text, Title } = Typography;

interface InventoryType {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface AttributeDefinition {
  id: string;
  name: string;
  label: string;
  data_type: string;
  required?: boolean;
  options?: string[];
  [key: string]: unknown;
}

interface Inventory {
  id: string;
  merchant_id?: string;
  product?: {
    id?: string;
    sku?: string;
    name?: string;
  };
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  available_quantity?: number;
  inventory_type?: string;
  inventory_type_id?: string;
  custom_attributes?: Record<string, unknown>;
  [key: string]: unknown;
}

interface TransformationItem {
  inventory_id: string;
  shelf_id: string;
  quantity: number;
  reason_code?: string;
  [key: string]: unknown;
}

interface ShelfInventoryEntry {
  shelf_id: string;
  shelf_location?: string;
  available_quantity?: number;
  quantity: number;
  transformQuantity: number;
  maxQty: number;
  [key: string]: unknown;
}

interface TransformationItemModalProps {
  visible: boolean;
  inventory: Inventory | null;
  onCancel: () => void;
  onConfirm?: (items: Array<Record<string, unknown>>) => void;
  defaultReasonCode?: string;
  existingItems?: TransformationItem[];
  standalone?: boolean;
  onSuccess?: () => void;
}

const TransformationItemModal: React.FC<TransformationItemModalProps> = ({
  visible,
  inventory,
  onCancel,
  onConfirm,
  defaultReasonCode,
  existingItems = [],
  standalone = false,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { merchantId } = useOperatorData() || {};
  const [form] = Form.useForm();
  const [loadingShelves, setLoadingShelves] = useState(false);
  const [shelfEntries, setShelfEntries] = useState<ShelfInventoryEntry[]>([]);
  const [inventoryTypes, setInventoryTypes] = useState<InventoryType[]>([]);
  const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>([]);
  const [totalQuantity, setTotalQuantity] = useState<number | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const targetBatch = Form.useWatch('target_batch', form);
  const targetExpiry = Form.useWatch('target_expiration_date', form);
  const targetManufacturing = Form.useWatch('target_manufacturing_date', form);
  const targetInventoryTypeId = Form.useWatch('target_inventory_type_id', form);
  const targetCustomAttributes = Form.useWatch('target_custom_attributes', form);

  useEffect(() => {
    if (visible && inventory) {
      form.resetFields();
      form.setFieldsValue({
        name: undefined,
        header_note: undefined,
        target_batch: inventory.batch,
        target_inventory_type_id: inventory.inventory_type_id,
        target_expiration_date: inventory.expiration_date ? dayjs(inventory.expiration_date) : null,
        target_manufacturing_date: inventory.manufacturing_date ? dayjs(inventory.manufacturing_date) : null,
        target_custom_attributes: inventory.custom_attributes || {},
        reason_code: defaultReasonCode || 'OTHER',
      });
      fetchShelfInventories();
      fetchInventoryTypes();
      fetchAttributeDefinitions();
    } else {
      setShelfEntries([]);
      setTotalQuantity(null);
      setSuggesting(false);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, inventory]);

  if (!inventory) return null;

  const getAllocatedForShelf = (shelfId: string): number => {
    return existingItems
      .filter(item => item.inventory_id === inventory.id && item.shelf_id === shelfId)
      .reduce((sum, item) => sum + Math.abs(item.quantity), 0);
  };

  const fetchShelfInventories = async () => {
    setLoadingShelves(true);
    try {
      const response = await inventoryService.getShelfInventories(inventory.id);
      if (response.success) {
        const list = response.data || [];

        // 可轉換最大量規則：
        // 1. 每個 shelf 的 max 不得超過該儲位實際可動量
        //    → 後端已回傳為 si.available_quantity；該值已扣掉「仍鎖在此儲位」的
        //      ShelfAllocation，且不重複扣掉已被撿進撿貨車(tote)的 stale 部分，
        //      因此與 inventory 層的可用量一致。
        // 2. 所有 shelf 加總 max 不得超過 inventory 層級的 available_quantity
        //    → 將 inventory budget 依序分配到各 shelf，每個 shelf 頂著自己的 shelfCap
        const existingForInventory = existingItems
          .filter(item => item.inventory_id === inventory.id)
          .reduce((sum, item) => sum + Math.abs(item.quantity), 0);
        let remaining = Math.max(0, (inventory.available_quantity ?? 0) - existingForInventory);

        const entries: ShelfInventoryEntry[] = list
          .map((si: { shelf_id: string; available_quantity?: number; [key: string]: unknown }) => {
            const shelfCap = Math.max(0, (si.available_quantity ?? 0) - getAllocatedForShelf(si.shelf_id));
            const maxQty = Math.min(shelfCap, remaining);
            remaining -= maxQty;
            return {
              ...si,
              quantity: si.available_quantity ?? 0,
              transformQuantity: maxQty,
              maxQty,
            };
          })
          .filter((e: ShelfInventoryEntry) => e.maxQty > 0);

        setShelfEntries(entries);
        setTotalQuantity(entries.reduce((sum, e) => sum + e.transformQuantity, 0));
      }
    } catch (error) {
      console.error(error);
      message.error(t('common.message.fetch_failed') as string);
    } finally {
      setLoadingShelves(false);
    }
  };

  const fetchInventoryTypes = async () => {
    try {
      const response = await inventoryTypeService.listAll();
      if (response.success) {
        const list = Array.isArray(response.data) ? response.data : (response.data.list || []);
        setInventoryTypes(list);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAttributeDefinitions = async () => {
    try {
      const response = await inventoryAttributeDefinitionService.list({ merchant_id: inventory.merchant_id });
      if (response.success) {
        setAttributeDefinitions((response.data || []) as unknown as AttributeDefinition[]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // --- Quantity management ---

  const maxTotal = shelfEntries.reduce((sum, e) => sum + e.maxQty, 0);

  const handleTotalQuantityChange = (value: number | null) => {
    setTotalQuantity(value);
    if (value == null) return;

    let remaining = value;
    setShelfEntries(prev => prev.map(e => {
      const allocated = Math.min(remaining, e.maxQty);
      remaining -= allocated;
      return { ...e, transformQuantity: allocated };
    }));
  };

  const handleQuantityChange = (shelfId: string, value: number | null) => {
    setShelfEntries(prev => {
      const updated = prev.map(e =>
        e.shelf_id === shelfId ? { ...e, transformQuantity: value ?? 0 } : e
      );
      setTotalQuantity(updated.reduce((sum, e) => sum + (e.transformQuantity || 0), 0));
      return updated;
    });
  };

  const handleClearShelf = (shelfId: string) => {
    handleQuantityChange(shelfId, 0);
  };

  const handleClearAll = () => {
    setTotalQuantity(0);
    setShelfEntries(prev => prev.map(e => ({ ...e, transformQuantity: 0 })));
  };

  const validEntries = shelfEntries.filter(e => e.transformQuantity > 0);
  const totalTransformQty = validEntries.reduce((sum, e) => sum + e.transformQuantity, 0);

  // Check if any target attribute differs from the source inventory
  const hasChanges = (() => {
    if (targetInventoryTypeId !== inventory.inventory_type_id) return true;
    if ((targetBatch ?? '') !== (inventory.batch ?? '')) return true;

    const fmtDate = (v: unknown) => (dayjs.isDayjs(v) ? (v as Dayjs).format('YYYY-MM-DD') : v) || null;
    if (fmtDate(targetExpiry) !== (inventory.expiration_date || null)) return true;
    if (fmtDate(targetManufacturing) !== (inventory.manufacturing_date || null)) return true;

    const orig: Record<string, unknown> = inventory.custom_attributes || {};
    const cur: Record<string, unknown> = targetCustomAttributes || {};
    const allKeys = new Set([...Object.keys(orig), ...Object.keys(cur)]);
    for (const key of allKeys) {
      if (String(orig[key] ?? '') !== String(cur[key] ?? '')) return true;
    }
    return false;
  })();

  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await inventoryTransformationService.suggestName(merchantId as unknown as null);
      if (res.success) {
        form.setFieldsValue({ name: res.data.name });
      } else {
        message.error((res.message || t('common.message.operation_failed')) as string);
      }
    } catch (error) {
      console.error(error);
      message.error(t('common.message.operation_failed') as string);
    } finally {
      setSuggesting(false);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (validEntries.length === 0) {
        message.error(t('shared.inventory_transformation.error.no_transform_quantity') as string);
        return;
      }

      const targetType = inventoryTypes.find(it => it.id === values.target_inventory_type_id);

      const items = validEntries.map(e => ({
        inventory_id: inventory.id,
        inventory: inventory,
        shelf_id: e.shelf_id,
        shelf: e,
        quantity: e.transformQuantity,
        reason_code: values.reason_code,
        note: standalone ? undefined : values.note,
        target_batch: values.target_batch,
        target_expiration_date: values.target_expiration_date ? values.target_expiration_date.format('YYYY-MM-DD') : null,
        target_manufacturing_date: values.target_manufacturing_date ? values.target_manufacturing_date.format('YYYY-MM-DD') : null,
        target_inventory_type_id: values.target_inventory_type_id,
        target_custom_attributes: values.target_custom_attributes,
        target_inventory_type_name: targetType?.name,
      }));

      if (standalone) {
        setSubmitting(true);
        try {
          const payload = {
            name: values.name,
            note: values.header_note,
            items: items.map(({ inventory: _inv, shelf: _s, target_inventory_type_name: _n, ...rest }) => rest),
          };
          const res = await inventoryTransformationService.create(payload);
          if (res.success) {
            const recordId = res.data?.id;
            if (recordId) {
              message.success(
                <span>
                  {t('common.message.create_success')}{' '}
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => navigate(`/inventory_transformations/${recordId}`)}
                  >
                    {t('shared.inventory_transformation.view_transformation')}
                  </Button>
                </span>,
                5,
              );
            } else {
              message.success(t('common.message.create_success') as string);
            }
            onSuccess?.();
            onCancel();
          } else {
            message.error((res.message || t('common.message.operation_failed')) as string);
          }
        } finally {
          setSubmitting(false);
        }
      } else {
        onConfirm?.(items);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // --- Render helpers ---

  const renderAttributeInput = (def: AttributeDefinition) => {
    const commonProps = {
      placeholder: def.data_type === 'select' ? (t('common.placeholder.select') as string) : '',
      style: { width: '100%' },
    };

    if (def.data_type === 'select') {
      return <Select {...commonProps} allowClear={!def.required} options={def.options?.map(opt => ({ label: opt, value: opt }))} />;
    } else if (def.data_type === 'number') {
      return <InputNumber {...commonProps} />;
    } else if (def.data_type === 'date') {
      return <DatePicker {...commonProps} format="YYYY-MM-DD" />;
    } else if (def.data_type === 'boolean') {
      // Boolean is always binary; Schema 層 (InventoryAttributeDefinition)
      // 已禁止 boolean + required=true，因此這裡永遠用 Switch。
      return <Switch />;
    }
    return <Input {...commonProps} />;
  };

  const currentInventoryTypeName = inventory.inventory_type;
  const targetInventoryType = inventoryTypes.find(it => it.id === targetInventoryTypeId);
  const targetInventoryTypeName = targetInventoryType?.name;

  const renderAttributeDiff = (label: string, original: unknown, current: unknown, icon?: ReactNode) => {
    let displayCurrent: unknown = current;
    if (dayjs.isDayjs(current)) displayCurrent = (current as Dayjs).format('YYYY-MM-DD');

    const isChanged = original !== displayCurrent && current !== undefined && current !== null;

    return (
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
          {icon && <span style={{ marginRight: 8, color: '#999' }}>{icon}</span>}
          <Text delete={isChanged} type={isChanged ? "secondary" : undefined} style={{ marginRight: 8 }}>
            {(original as ReactNode) || '-'}
          </Text>
          {isChanged && (
            <>
              <ArrowRightOutlined style={{ fontSize: 10, color: '#1890ff', margin: '0 8px' }} />
              <Text strong style={{ color: '#1890ff' }}>{displayCurrent as ReactNode}</Text>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCustomAttributesDiff = () => {
    if (!attributeDefinitions.length) return null;
    return attributeDefinitions.map(def => {
      const original = inventory.custom_attributes?.[def.name];
      const current = targetCustomAttributes?.[def.name];
      return <div key={def.id}>{renderAttributeDiff(def.label, original, current)}</div>;
    });
  };

  const shelfColumns = [
    {
      title: t('models.shelf.location'),
      dataIndex: 'shelf_location',
      width: 140,
    },
    {
      title: t('models.inventory.quantity'),
      dataIndex: 'maxQty',
      width: 80,
      align: 'right' as const,
    },
    {
      title: t('shared.inventory_transformation.movement_quantity'),
      width: 150,
      render: (_: unknown, r: ShelfInventoryEntry) => (
        <Space size={4}>
          <InputNumber
            min={0}
            max={r.maxQty}
            value={r.transformQuantity}
            onChange={(val) => handleQuantityChange(r.shelf_id, val)}
            size="small"
            style={{ width: 90 }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>/ {r.maxQty}</Text>
        </Space>
      ),
    },
    {
      title: '',
      width: 40,
      render: (_: unknown, r: ShelfInventoryEntry) => (
        <Button
          type="text"
          size="small"
          icon={<CloseCircleOutlined style={{ color: '#999' }} />}
          onClick={() => handleClearShelf(r.shelf_id)}
          disabled={r.transformQuantity === 0}
        />
      ),
    },
  ];

  return (
    <Modal
      title={standalone ? t('shared.inventory_transformation.quick_transform_title') : t('shared.inventory_transformation.add_item')}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText={t('common.action.confirm')}
      cancelText={t('common.action.cancel')}
      okButtonProps={{ disabled: !hasChanges || validEntries.length === 0, loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      maskClosable={!submitting}
      width={900}
    >
      {/* Product Info */}
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }} bordered={false}>
        <Space>
          {inventory.product?.sku && <Tag color="blue">{inventory.product.sku}</Tag>}
          {inventory.product?.name && <Text>{inventory.product.name}</Text>}
        </Space>
        <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
          {inventory.batch && <span>{t('models.inventory.batch')}: {inventory.batch}</span>}
          {inventory.expiration_date && <span style={{ marginLeft: 12 }}>{t('models.inventory.expiration_date')}: {inventory.expiration_date}</span>}
        </div>
      </Card>

      {/* Standalone-only: 單頭基本資料（單號 + 單頭備註） */}
      {standalone && (
        <Form form={form} layout="vertical" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('models.inventory_transformation.name')}>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="name" noStyle>
                    <Input placeholder={t('shared.inventory_transformation.name_placeholder') as string} />
                  </Form.Item>
                  <Button onClick={handleSuggestName} loading={suggesting}>
                    {t('shared.inventory_transformation.suggest_name')}
                  </Button>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('shared.inventory_transformation.quick_transform_header_note')} name="header_note">
                <Input.TextArea rows={1} placeholder={t('common.placeholder.note') as string} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}

      <Row gutter={24}>
        {/* Left: Target Attribute Form */}
        <Col span={14}>
          <Form form={form} layout="vertical">
            <Title level={5} style={{ marginBottom: 16 }}>{t('shared.inventory_transformation.target_attributes')}</Title>

            <Form.Item label={t('models.inventory.inventory_type')} name="target_inventory_type_id">
              <Select
                options={inventoryTypes.map(it => ({ label: it.name, value: it.id }))}
                placeholder={t('common.placeholder.select') as string}
              />
            </Form.Item>

            <Form.Item label={t('models.inventory.batch')} name="target_batch">
              <Input />
            </Form.Item>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item label={t('models.inventory.manufacturing_date')} name="target_manufacturing_date">
                  <DatePicker style={{ width: '100%' }} placeholder={t('common.placeholder.select_date') as string} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('models.inventory.expiration_date')} name="target_expiration_date">
                  <DatePicker style={{ width: '100%' }} placeholder={t('common.placeholder.select_date') as string} />
                </Form.Item>
              </Col>
            </Row>

            {/* Custom Attributes */}
            {attributeDefinitions.length > 0 && (
              <>
                <Divider dashed style={{ margin: '12px 0' }}>{t('models.inventory.custom_attributes')}</Divider>
                {attributeDefinitions.map(def => {
                  // Schema 層 (InventoryAttributeDefinition) 已禁 boolean+required=true，
                  // boolean 永遠用 Switch（`checked` + implicit false default）。
                  const isBoolean = def.data_type === 'boolean';
                  return (
                  <Form.Item
                    key={def.id}
                    name={['target_custom_attributes', def.name]}
                    label={def.label}
                    rules={[{ required: def.required, message: t('common.validation.required') as string }]}
                    valuePropName={isBoolean ? 'checked' : 'value'}
                    initialValue={isBoolean ? false : undefined}
                    getValueProps={(value) => {
                      if (def.data_type === 'date' && value) return { value: dayjs(value) };
                      if (def.data_type === 'boolean') return { checked: !!value };
                      return { value };
                    }}
                    getValueFromEvent={(event) => {
                      if (def.data_type === 'date' && event) return event.format('YYYY-MM-DD');
                      if (def.data_type === 'boolean') return !!event;   // Switch onChange → boolean
                      if (event && event.target) return event.target.value;
                      return event;
                    }}
                  >
                    {renderAttributeInput(def)}
                  </Form.Item>
                  );
                })}
              </>
            )}

            <Divider dashed />

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={t('models.stock_adjustment.reason')} name="reason_code" rules={[{ required: true }]}>
                  <Select>
                    {REASON_CODES.map((code: string) => (
                      <Select.Option key={code} value={code}>{t(`enums.stock_adjustment.reason_code.${code}`)}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {!standalone && (
                <Col span={12}>
                  <Form.Item label={t('models.stock_adjustment.note')} name="note">
                    <Input.TextArea rows={2} placeholder={t('common.placeholder.note') as string} />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        </Col>

        {/* Right: Diff Preview */}
        <Col span={10}>
          <Card size="small" style={{ background: '#fafafa' }}>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {renderAttributeDiff(t('models.inventory.inventory_type') as string, currentInventoryTypeName, targetInventoryTypeName, <SwapOutlined />)}
              {renderAttributeDiff(t('models.inventory.batch') as string, inventory.batch, targetBatch, <NumberOutlined />)}
              {renderAttributeDiff(t('models.inventory.manufacturing_date') as string, inventory.manufacturing_date, targetManufacturing, <CalendarOutlined />)}
              {renderAttributeDiff(t('models.inventory.expiration_date') as string, inventory.expiration_date, targetExpiry, <CalendarOutlined />)}
              {renderCustomAttributesDiff()}
            </div>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ marginTop: 8 }}>
              {inventory.product?.sku && <Tag color="blue">{inventory.product.sku}</Tag>}
              {inventory.product?.name && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>{inventory.product.name}</div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* No changes warning */}
      {!hasChanges && (
        <Alert
          message={t('shared.inventory_transformation.error.no_attribute_change')}
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {/* Shelf Quantity Table */}
      <Divider dashed style={{ margin: '16px 0 12px' }}>{t('models.shelf.location')}</Divider>

      {/* Total quantity input + clear all */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Space>
          <Text>{t('shared.inventory_transformation.total_quantity')}</Text>
          <InputNumber
            min={0}
            max={maxTotal}
            value={totalQuantity}
            onChange={handleTotalQuantityChange}
            style={{ width: 120 }}
            size="small"
          />
          <Text type="secondary">/ {maxTotal}</Text>
        </Space>
        <Button size="small" onClick={handleClearAll}>
          {t('shared.inventory_transformation.clear_all')}
        </Button>
      </div>

      <Table
        columns={shelfColumns}
        dataSource={shelfEntries}
        rowKey="shelf_id"
        loading={loadingShelves}
        pagination={false}
        size="small"
        style={{ marginBottom: 16 }}
      />

      {validEntries.length > 0 && (
        <Alert
          message={t('shared.inventory_transformation.transformation_summary', { count: validEntries.length, total: totalTransformQty })}
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};

export default TransformationItemModal;
